import React, { useRef, useCallback, useMemo, useState, useEffect } from 'react';
import i18n from 'i18n-js';

import { theme } from 'config/theme';
import { WallSide, EntrancePosition } from '../context';

// SVG canvas dimensions (user units) — exported so siblings share one source of truth
export const SVG_WIDTH = 800;
export const SVG_HEIGHT = 500;

// Room bounds — ~40 px margins on all sides so the entrance label/arc fits outside the wall
export const ROOM = {
    x: 40,
    y: 40,
    width: 720,
    height: 420,
};

const WALL_STROKE = 6;
const WALL_COLOR = '#9E9E9E';
const MAGENTA = theme.palette.primary.main; // #99567E
const GRID_SIZE = 40;
const ENTRANCE_GAP = 32; // notch gap in wall (px)
const ENTRANCE_RADIUS = 22; // door arc radius
// SVG canvas background — used to erase the wall at the notch during drag
const CANVAS_BG = '#fafafa';

// Zoom config
export const ZOOM_MIN = 0.5;
export const ZOOM_MAX = 2.0;
export const ZOOM_STEP = 0.25;

interface MapEditorCanvasProps {
    gridEnabled: boolean;
    zoom: number;
    entrance: EntrancePosition | null;
    isMarkingEntrance: boolean;
    onSelectWall: (wall: WallSide, position: number) => void;
    onMoveEntrance: (position: number) => void;
    children?: React.ReactNode;
    /** Optional external ref so callers can do their own clientToSVG conversions */
    svgRef?: React.RefObject<SVGSVGElement>;
    /**
     * When true: the entrance drag handle and wall hitboxes are rendered so the
     * user can reposition the entrance. Pass only on the Map Configuration step.
     * Defaults to false so placement/details steps render the entrance as static.
     */
    allowEntranceDrag?: boolean;
}

/** Convert a client-space pointer event position to SVG user units */
function clientToSVG(
    svg: SVGSVGElement,
    clientX: number,
    clientY: number
): { x: number; y: number } {
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const inv = ctm.inverse();
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const svgPt = pt.matrixTransform(inv);
    return { x: svgPt.x, y: svgPt.y };
}

/** Compute normalized wall position (0..1) from an SVG point for the given wall */
function normalizedPosition(wall: WallSide, svgX: number, svgY: number): number {
    switch (wall) {
        case 'top':
        case 'bottom':
            return Math.min(1, Math.max(0, (svgX - ROOM.x) / ROOM.width));
        case 'left':
        case 'right':
            return Math.min(1, Math.max(0, (svgY - ROOM.y) / ROOM.height));
    }
}

/** Get the SVG x,y of an entrance marker center on a given wall + position */
function entrancePoint(wall: WallSide, position: number): { x: number; y: number } {
    switch (wall) {
        case 'top':
            return { x: ROOM.x + position * ROOM.width, y: ROOM.y };
        case 'bottom':
            return { x: ROOM.x + position * ROOM.width, y: ROOM.y + ROOM.height };
        case 'left':
            return { x: ROOM.x, y: ROOM.y + position * ROOM.height };
        case 'right':
            return { x: ROOM.x + ROOM.width, y: ROOM.y + position * ROOM.height };
    }
}

/**
 * Renders the full magenta wall segment for the entrance wall — no gap.
 * Rendered OUTSIDE the drag-translated group so it stays fixed during drag.
 */
const EntranceWallHighlight = React.memo(function EntranceWallHighlight({
    entrance,
    isMarkingEntrance,
}: {
    entrance: EntrancePosition;
    isMarkingEntrance: boolean;
}) {
    const stroke = isMarkingEntrance ? '#ccaabb' : MAGENTA;
    switch (entrance.wall) {
        case 'top':
            return (
                <line
                    x1={ROOM.x}
                    y1={ROOM.y}
                    x2={ROOM.x + ROOM.width}
                    y2={ROOM.y}
                    stroke={stroke}
                    strokeWidth={WALL_STROKE}
                    strokeLinecap="square"
                />
            );
        case 'bottom':
            return (
                <line
                    x1={ROOM.x}
                    y1={ROOM.y + ROOM.height}
                    x2={ROOM.x + ROOM.width}
                    y2={ROOM.y + ROOM.height}
                    stroke={stroke}
                    strokeWidth={WALL_STROKE}
                    strokeLinecap="square"
                />
            );
        case 'left':
            return (
                <line
                    x1={ROOM.x}
                    y1={ROOM.y}
                    x2={ROOM.x}
                    y2={ROOM.y + ROOM.height}
                    stroke={stroke}
                    strokeWidth={WALL_STROKE}
                    strokeLinecap="square"
                />
            );
        case 'right':
            return (
                <line
                    x1={ROOM.x + ROOM.width}
                    y1={ROOM.y}
                    x2={ROOM.x + ROOM.width}
                    y2={ROOM.y + ROOM.height}
                    stroke={stroke}
                    strokeWidth={WALL_STROKE}
                    strokeLinecap="square"
                />
            );
    }
});

/**
 * Renders the entrance marker: notch (gap in wall), door-jamb ticks, door arc,
 * door leaf, crosshair, and label pill.
 *
 * This component lives INSIDE the drag-translated group so only the marker
 * moves during a live drag — the wall highlight (EntranceWallHighlight) stays put.
 */
const EntranceMarker = React.memo(function EntranceMarker({
    entrance,
}: {
    entrance: EntrancePosition;
}) {
    const { wall, position } = entrance;
    const { x: cx, y: cy } = entrancePoint(wall, position);

    let wallDx = 0;
    let wallDy = 0;
    let inDx = 0;
    let inDy = 0;
    let outDx = 0;
    let outDy = 0;

    switch (wall) {
        case 'top':
            wallDx = 1;
            inDy = 1;
            outDy = -1;
            break;
        case 'bottom':
            wallDx = 1;
            inDy = -1;
            outDy = 1;
            break;
        case 'left':
            wallDy = 1;
            inDx = 1;
            outDx = -1;
            break;
        case 'right':
            wallDy = 1;
            inDx = -1;
            outDx = 1;
            break;
    }

    const half = ENTRANCE_GAP / 2;
    const n1x = cx - wallDx * half;
    const n1y = cy - wallDy * half;
    const n2x = cx + wallDx * half;
    const n2y = cy + wallDy * half;

    const TICK = 8;
    const t1ax = n1x + inDx * TICK;
    const t1ay = n1y + inDy * TICK;
    const t2ax = n2x + inDx * TICK;
    const t2ay = n2y + inDy * TICK;

    const arcEndX = n1x + outDx * ENTRANCE_RADIUS;
    const arcEndY = n1y + outDy * ENTRANCE_RADIUS;

    const labelOffset = 26;
    const labelX = cx + outDx * labelOffset;
    const labelY = cy + outDy * labelOffset;

    const ch = 7;
    const isHoriz = wall === 'top' || wall === 'bottom';

    // Notch erase rect: covers the wall at the gap with the canvas background so
    // the full wall highlight behind appears to have a gap at the marker position.
    const notchRect = isHoriz ? (
        <rect
            x={n1x}
            y={cy - WALL_STROKE / 2}
            width={ENTRANCE_GAP}
            height={WALL_STROKE}
            fill={CANVAS_BG}
        />
    ) : (
        <rect
            x={cx - WALL_STROKE / 2}
            y={n1y}
            width={WALL_STROKE}
            height={ENTRANCE_GAP}
            fill={CANVAS_BG}
        />
    );

    return (
        <g>
            {/* Erase the wall at the notch position */}
            {notchRect}

            {/* Door-jamb ticks at gap edges */}
            <line
                x1={n1x}
                y1={n1y}
                x2={t1ax}
                y2={t1ay}
                stroke={MAGENTA}
                strokeWidth={2.5}
                strokeLinecap="round"
            />
            <line
                x1={n2x}
                y1={n2y}
                x2={t2ax}
                y2={t2ay}
                stroke={MAGENTA}
                strokeWidth={2.5}
                strokeLinecap="round"
            />

            {/* Door arc: quarter-circle from n1 hinge sweeping OUTWARD */}
            <path
                d={`M ${n1x} ${n1y} A ${ENTRANCE_RADIUS} ${ENTRANCE_RADIUS} 0 0 0 ${arcEndX} ${arcEndY}`}
                stroke={MAGENTA}
                strokeWidth={1.5}
                fill="none"
                strokeLinecap="round"
                strokeDasharray="4 3"
            />

            {/* Door leaf line from hinge to arc end */}
            <line
                x1={n1x}
                y1={n1y}
                x2={arcEndX}
                y2={arcEndY}
                stroke={MAGENTA}
                strokeWidth={1.5}
                strokeLinecap="round"
            />

            {/* Crosshair at entrance center */}
            <line x1={cx - ch} y1={cy} x2={cx + ch} y2={cy} stroke={MAGENTA} strokeWidth={2} />
            <line x1={cx} y1={cy - ch} x2={cx} y2={cy + ch} stroke={MAGENTA} strokeWidth={2} />

            {/* Label pill — OUTSIDE the wall */}
            <rect x={labelX - 34} y={labelY - 11} width={68} height={22} rx={11} fill={MAGENTA} />
            <text
                x={labelX}
                y={labelY + 4}
                textAnchor="middle"
                fontSize={11}
                fontWeight={600}
                fill="white"
                fontFamily="-apple-system, BlinkMacSystemFont, Roboto, Segoe UI, sans-serif"
            >
                {i18n.t('tableMaps.wizard.entrance')}
            </text>
        </g>
    );
});

// Minimum SVG units of room that must remain visible after panning
const PAN_MIN_VISIBLE = 80;
// Pointer movement (client px) below which a pointerdown→up is treated as a click
const PAN_CLICK_THRESHOLD = 3;

/**
 * Clamp a pan offset so at least PAN_MIN_VISIBLE SVG units of the room stay
 * inside the viewport. When zoom <= 1 the entire map fits, so pan is always 0.
 */
function clampPan(px: number, py: number, currentZoom: number): { x: number; y: number } {
    if (currentZoom <= 1) return { x: 0, y: 0 };
    const vbW = SVG_WIDTH / currentZoom;
    const vbH = SVG_HEIGHT / currentZoom;
    const vbX0 = (SVG_WIDTH - vbW) / 2;
    const vbY0 = (SVG_HEIGHT - vbH) / 2;
    // viewBox left = vbX0 - panX, right = vbX0 - panX + vbW
    // Ensure at least PAN_MIN_VISIBLE of the room is visible
    const maxPanX = vbX0 + vbW - ROOM.x - PAN_MIN_VISIBLE;
    const minPanX = vbX0 - (ROOM.x + ROOM.width) + PAN_MIN_VISIBLE;
    const maxPanY = vbY0 + vbH - ROOM.y - PAN_MIN_VISIBLE;
    const minPanY = vbY0 - (ROOM.y + ROOM.height) + PAN_MIN_VISIBLE;
    return {
        x: Math.min(maxPanX, Math.max(minPanX, px)),
        y: Math.min(maxPanY, Math.max(minPanY, py)),
    };
}

function MapEditorCanvas({
    gridEnabled,
    zoom,
    entrance,
    isMarkingEntrance,
    onSelectWall,
    onMoveEntrance,
    children,
    svgRef: externalSvgRef,
    allowEntranceDrag = false,
}: MapEditorCanvasProps) {
    const internalSvgRef = useRef<SVGSVGElement>(null);
    const svgRef = externalSvgRef ?? internalSvgRef;

    // Ref to the entrance drag handle element for imperative transform updates
    const entranceDragRef = useRef<SVGCircleElement>(null);
    // Ref to the <g> wrapping EntranceMarker — translated imperatively during drag
    const entranceGRef = useRef<SVGGElement>(null);
    // Committed entrance SVG point captured at drag start (avoids stale closures)
    const entranceDragCommittedPt = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
    // Track drag state in a ref — no re-render on every pointermove
    const entranceDragActive = useRef(false);
    const entranceDragWall = useRef<WallSide | null>(null);

    // Pan offset in SVG user units.
    // Refs are updated imperatively on every pointermove (no re-render per frame).
    // State is committed once on pointerup to trigger a React re-render.
    const panXRef = useRef(0);
    const panYRef = useRef(0);
    const [panState, setPanState] = useState({ x: 0, y: 0 });

    // Pan drag tracking refs
    const panDragActive = useRef(false);
    const panDidMove = useRef(false);
    const panStartClient = useRef({ x: 0, y: 0 });
    const panStartPan = useRef({ x: 0, y: 0 });

    // Reset / re-clamp pan whenever zoom changes
    useEffect(() => {
        const clamped = clampPan(panXRef.current, panYRef.current, zoom);
        panXRef.current = clamped.x;
        panYRef.current = clamped.y;
        setPanState(clamped);
    }, [zoom]); // eslint-disable-line react-hooks/exhaustive-deps

    // Effective viewBox dimensions based on zoom (zoom out = larger viewBox)
    const vbWidth = SVG_WIDTH / zoom;
    const vbHeight = SVG_HEIGHT / zoom;
    const vbX = (SVG_WIDTH - vbWidth) / 2;
    const vbY = (SVG_HEIGHT - vbHeight) / 2;
    const viewBox = `${vbX - panState.x} ${vbY - panState.y} ${vbWidth} ${vbHeight}`;

    const hitboxThickness = 24;

    // Wall hitbox rects: [x, y, width, height, wall]
    const wallHitboxes: Array<{ wall: WallSide; x: number; y: number; w: number; h: number }> =
        useMemo(
            () => [
                {
                    wall: 'top' as WallSide,
                    x: ROOM.x,
                    y: ROOM.y - hitboxThickness / 2,
                    w: ROOM.width,
                    h: hitboxThickness,
                },
                {
                    wall: 'bottom' as WallSide,
                    x: ROOM.x,
                    y: ROOM.y + ROOM.height - hitboxThickness / 2,
                    w: ROOM.width,
                    h: hitboxThickness,
                },
                {
                    wall: 'left' as WallSide,
                    x: ROOM.x - hitboxThickness / 2,
                    y: ROOM.y,
                    w: hitboxThickness,
                    h: ROOM.height,
                },
                {
                    wall: 'right' as WallSide,
                    x: ROOM.x + ROOM.width - hitboxThickness / 2,
                    y: ROOM.y,
                    w: hitboxThickness,
                    h: ROOM.height,
                },
            ],
            []
        );

    // Pan handlers — attached to the SVG root. Children that own their own drag
    // (tables, entrance circle) call stopPropagation so pan never starts from them.
    function handleSVGPointerDown(e: React.PointerEvent<SVGSVGElement>) {
        // Only pan when zoomed in and not in entrance-marking mode
        if (zoom <= 1 || isMarkingEntrance || entranceDragActive.current) return;
        panDragActive.current = true;
        panDidMove.current = false;
        panStartClient.current = { x: e.clientX, y: e.clientY };
        panStartPan.current = { x: panXRef.current, y: panYRef.current };
        (e.currentTarget as SVGSVGElement).setPointerCapture(e.pointerId);
    }

    function handleSVGPointerMove(e: React.PointerEvent<SVGSVGElement>) {
        if (!panDragActive.current) return;
        const dx = e.clientX - panStartClient.current.x;
        const dy = e.clientY - panStartClient.current.y;
        if (!panDidMove.current && Math.hypot(dx, dy) > PAN_CLICK_THRESHOLD) {
            panDidMove.current = true;
        }
        if (!panDidMove.current) return;

        const svgEl = svgRef.current;
        if (!svgEl) return;
        const rect = svgEl.getBoundingClientRect();
        // Convert client pixels → SVG user units (drag right moves content right = panX increases)
        const scaleX = vbWidth / rect.width;
        const scaleY = vbHeight / rect.height;
        const rawX = panStartPan.current.x + dx * scaleX;
        const rawY = panStartPan.current.y + dy * scaleY;
        const clamped = clampPan(rawX, rawY, zoom);
        panXRef.current = clamped.x;
        panYRef.current = clamped.y;
        // Imperative update — no setState, no re-render per frame
        svgEl.setAttribute(
            'viewBox',
            `${vbX - clamped.x} ${vbY - clamped.y} ${vbWidth} ${vbHeight}`
        );
        svgEl.style.cursor = 'grabbing';
    }

    function handleSVGPointerUp() {
        if (!panDragActive.current) return;
        panDragActive.current = false;
        if (panDidMove.current) {
            // Commit to state — triggers React re-render which also resets cursor via style prop
            setPanState({ x: panXRef.current, y: panYRef.current });
        }
    }

    function getSVGPoint(clientX: number, clientY: number): { x: number; y: number } {
        if (!svgRef.current) return { x: 0, y: 0 };
        return clientToSVG(svgRef.current, clientX, clientY);
    }

    function handleWallClick(wall: WallSide, e: React.PointerEvent<SVGElement>) {
        if (!isMarkingEntrance) return;
        const pt = getSVGPoint(e.clientX, e.clientY);
        const pos = normalizedPosition(wall, pt.x, pt.y);
        onSelectWall(wall, pos);
    }

    // Entrance drag: imperative — no React state changes during pointermove
    const handleEntranceDragStart = useCallback(
        (e: React.PointerEvent<SVGCircleElement>) => {
            if (!entrance) return;
            e.stopPropagation();
            e.currentTarget.setPointerCapture(e.pointerId);
            entranceDragActive.current = true;
            entranceDragWall.current = entrance.wall;
            // Capture the committed SVG position for delta computation during drag
            entranceDragCommittedPt.current = entrancePoint(entrance.wall, entrance.position);
        },
        [entrance]
    );

    const handleEntranceDragMove = useCallback((e: React.PointerEvent<SVGCircleElement>) => {
        if (!entranceDragActive.current || !entranceDragWall.current || !svgRef.current) return;
        const pt = clientToSVG(svgRef.current, e.clientX, e.clientY);
        const pos = normalizedPosition(entranceDragWall.current, pt.x, pt.y);
        const wall = entranceDragWall.current;
        const ep = entrancePoint(wall, pos);

        // Imperatively translate only the marker group — wall highlight stays fixed
        const dx = ep.x - entranceDragCommittedPt.current.x;
        const dy = ep.y - entranceDragCommittedPt.current.y;
        if (entranceGRef.current) {
            entranceGRef.current.setAttribute('transform', `translate(${dx}, ${dy})`);
        }
        // Also move the invisible drag handle circle
        if (entranceDragRef.current) {
            entranceDragRef.current.setAttribute('cx', String(ep.x));
            entranceDragRef.current.setAttribute('cy', String(ep.y));
        }
        // Store the live position for commit on pointerup
        (e.currentTarget as SVGCircleElement).dataset.livePos = String(pos);
    }, []);

    const handleEntranceDragEnd = useCallback(
        (e: React.PointerEvent<SVGCircleElement>) => {
            if (!entranceDragActive.current || !entranceDragWall.current || !svgRef.current) return;
            const pt = clientToSVG(svgRef.current, e.clientX, e.clientY);
            const pos = normalizedPosition(entranceDragWall.current, pt.x, pt.y);
            entranceDragActive.current = false;
            entranceDragWall.current = null;
            // Reset the marker group transform before React re-renders at new position
            if (entranceGRef.current) {
                entranceGRef.current.setAttribute('transform', '');
            }
            // Commit final position to context — single dispatch on pointerup
            onMoveEntrance(pos);
        },
        [onMoveEntrance]
    );

    // Full-canvas dot grid — rendered as very first child so it sits behind everything
    const fullCanvasGrid = useMemo(
        () =>
            gridEnabled ? (
                <rect x={0} y={0} width={SVG_WIDTH} height={SVG_HEIGHT} fill="url(#dotGrid)" />
            ) : null,
        [gridEnabled]
    );

    const staticWalls = useMemo(() => {
        // Walls are always rendered; entrance wall overridden by EntranceWallHighlight
        const walls: Array<{ wall: WallSide; x1: number; y1: number; x2: number; y2: number }> = [
            { wall: 'top', x1: ROOM.x, y1: ROOM.y, x2: ROOM.x + ROOM.width, y2: ROOM.y },
            {
                wall: 'bottom',
                x1: ROOM.x,
                y1: ROOM.y + ROOM.height,
                x2: ROOM.x + ROOM.width,
                y2: ROOM.y + ROOM.height,
            },
            { wall: 'left', x1: ROOM.x, y1: ROOM.y, x2: ROOM.x, y2: ROOM.y + ROOM.height },
            {
                wall: 'right',
                x1: ROOM.x + ROOM.width,
                y1: ROOM.y,
                x2: ROOM.x + ROOM.width,
                y2: ROOM.y + ROOM.height,
            },
        ];

        return walls.map(({ wall, x1, y1, x2, y2 }) => (
            <line
                key={wall}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={isMarkingEntrance ? '#ccaabb' : WALL_COLOR}
                strokeWidth={WALL_STROKE}
                strokeLinecap="square"
            />
        ));
    }, [isMarkingEntrance]);

    const staticCorners = useMemo(
        () => (
            <g opacity={0.12} pointerEvents="none">
                {/* top-left */}
                <path
                    d={`M ${ROOM.x} ${ROOM.y} C ${ROOM.x + 30} ${ROOM.y}, ${ROOM.x} ${
                        ROOM.y + 30
                    }, ${ROOM.x} ${ROOM.y}`}
                    fill={MAGENTA}
                />
                <path
                    d={`M ${ROOM.x} ${ROOM.y} C ${ROOM.x + 24} ${ROOM.y + 8}, ${ROOM.x + 8} ${
                        ROOM.y + 24
                    }, ${ROOM.x} ${ROOM.y}`}
                    fill={MAGENTA}
                />
                {/* top-right */}
                <path
                    d={`M ${ROOM.x + ROOM.width} ${ROOM.y} C ${ROOM.x + ROOM.width - 30} ${
                        ROOM.y
                    }, ${ROOM.x + ROOM.width} ${ROOM.y + 30}, ${ROOM.x + ROOM.width} ${ROOM.y}`}
                    fill={MAGENTA}
                />
                <path
                    d={`M ${ROOM.x + ROOM.width} ${ROOM.y} C ${ROOM.x + ROOM.width - 24} ${
                        ROOM.y + 8
                    }, ${ROOM.x + ROOM.width - 8} ${ROOM.y + 24}, ${ROOM.x + ROOM.width} ${ROOM.y}`}
                    fill={MAGENTA}
                />
                {/* bottom-left */}
                <path
                    d={`M ${ROOM.x} ${ROOM.y + ROOM.height} C ${ROOM.x + 30} ${
                        ROOM.y + ROOM.height
                    }, ${ROOM.x} ${ROOM.y + ROOM.height - 30}, ${ROOM.x} ${ROOM.y + ROOM.height}`}
                    fill={MAGENTA}
                />
                <path
                    d={`M ${ROOM.x} ${ROOM.y + ROOM.height} C ${ROOM.x + 24} ${
                        ROOM.y + ROOM.height - 8
                    }, ${ROOM.x + 8} ${ROOM.y + ROOM.height - 24}, ${ROOM.x} ${
                        ROOM.y + ROOM.height
                    }`}
                    fill={MAGENTA}
                />
                {/* bottom-right */}
                <path
                    d={`M ${ROOM.x + ROOM.width} ${ROOM.y + ROOM.height} C ${
                        ROOM.x + ROOM.width - 30
                    } ${ROOM.y + ROOM.height}, ${ROOM.x + ROOM.width} ${
                        ROOM.y + ROOM.height - 30
                    }, ${ROOM.x + ROOM.width} ${ROOM.y + ROOM.height}`}
                    fill={MAGENTA}
                />
                <path
                    d={`M ${ROOM.x + ROOM.width} ${ROOM.y + ROOM.height} C ${
                        ROOM.x + ROOM.width - 24
                    } ${ROOM.y + ROOM.height - 8}, ${ROOM.x + ROOM.width - 8} ${
                        ROOM.y + ROOM.height - 24
                    }, ${ROOM.x + ROOM.width} ${ROOM.y + ROOM.height}`}
                    fill={MAGENTA}
                />
            </g>
        ),
        []
    );

    // Wall hitboxes — only rendered when allowEntranceDrag and in marking mode
    const wallHitboxLayer = useMemo(
        () =>
            allowEntranceDrag && isMarkingEntrance
                ? wallHitboxes.map(({ wall, x, y, w, h }) => (
                      <rect
                          key={wall}
                          x={x}
                          y={y}
                          width={w}
                          height={h}
                          fill="transparent"
                          style={{ cursor: 'crosshair' }}
                          onPointerDown={(e) => handleWallClick(wall, e)}
                      />
                  ))
                : null,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [allowEntranceDrag, isMarkingEntrance, wallHitboxes]
    );

    const entrancePt = entrance ? entrancePoint(entrance.wall, entrance.position) : { x: 0, y: 0 };

    const svgCursor = isMarkingEntrance ? 'crosshair' : zoom > 1 ? 'grab' : 'default';

    return (
        <svg
            ref={svgRef}
            viewBox={viewBox}
            style={{
                width: '100%',
                height: '100%',
                display: 'block',
                background: CANVAS_BG,
                cursor: svgCursor,
            }}
            onPointerDown={handleSVGPointerDown}
            onPointerMove={handleSVGPointerMove}
            onPointerUp={handleSVGPointerUp}
        >
            <defs>
                {/* Dot grid pattern — anchored at SVG origin so it tiles the whole canvas */}
                <pattern
                    id="dotGrid"
                    x={0}
                    y={0}
                    width={GRID_SIZE}
                    height={GRID_SIZE}
                    patternUnits="userSpaceOnUse"
                >
                    <circle cx={GRID_SIZE / 2} cy={GRID_SIZE / 2} r={1.5} fill="#c8c8c8" />
                </pattern>
            </defs>

            {/* Full-canvas grid background — behind walls and tables */}
            {fullCanvasGrid}

            {/* Walls */}
            {staticWalls}

            {/* Entrance wall highlight (full wall, no gap) — stays fixed during drag */}
            {entrance && (
                <EntranceWallHighlight entrance={entrance} isMarkingEntrance={isMarkingEntrance} />
            )}

            {/* Entrance marker (notch + door elements) — translated imperatively during drag */}
            <g ref={entranceGRef}>{entrance && <EntranceMarker entrance={entrance} />}</g>

            {staticCorners}

            {/* Children (table placement layer) */}
            {children}

            {/* Wall hitboxes — only in entrance-drag mode so user can click a wall */}
            {wallHitboxLayer}

            {/* Drag handle on the entrance crosshair — only when allowEntranceDrag */}
            {allowEntranceDrag && entrance && (
                <circle
                    ref={entranceDragRef}
                    cx={entrancePt.x}
                    cy={entrancePt.y}
                    r={18}
                    fill="transparent"
                    style={{ cursor: 'grab' }}
                    onPointerDown={handleEntranceDragStart}
                    onPointerMove={handleEntranceDragMove}
                    onPointerUp={handleEntranceDragEnd}
                />
            )}
        </svg>
    );
}

export default React.memo(MapEditorCanvas);
